import React, { useEffect, useId } from 'react';
import type { AccessibilityRole, Role } from 'react-native';
import { useLingui } from '@lingui/react/macro';

import KEYS from '@rover/constants/src/platform/keys.constants';
import Check from '@rover/icons/svg/check.svg';
import { Flex } from '@rover/kibble/core';
import type { SizeSystemType, SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import {
  FeedbackMessage,
  FeedbackMessageProps,
  PrimaryLabel,
  SecondaryLabel,
} from '@rover/kibble/forms/fields/sharedTypography';
import { isReactNative } from '@rover/rsdk/src/modules/Network/userAgent';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

import type { ChipSize, ChipType } from '..';
import FilterChip, { type OptionProps } from '../Chip';

/**
 * Use this component to render a group of filter chips that can be selected or deselected,
 * and which has an immediate effect on another part of the page via onChange.
 *
 * This component should NOT be used as a form input where the value is submitted elsewhere.
 * For a single-choice form input, use RadioField with `variant="radio-pill"`; for a
 * multiple-choice one, a chip variant of CheckboxField still needs creating.
 */

type FilterChipGroupProps = {
  groupLabel: string;
  groupSecondaryLabel?: string;
  /**
   * Web only. Id of the element describing the group, for text the caller
   * draws itself. A screen reader reaching a chip directly would otherwise
   * meet it with no more than the group's name. Native has no equivalent of
   * `aria-describedby`; a native caller needs the description as text.
   */
  describedById?: string;
  showLabel?: boolean;
  options: OptionProps[];
  canSelectMultiple: boolean;
  isDeselectable?: boolean; // Whether selected chips can be deselected without selecting a new one
  // When true, each chip stays an individual Tab stop so Tab moves between chips
  // (the ARIA checkbox-group pattern). When false (default), the group behaves as
  // a single composite widget: Tab exits it and arrow keys move between chips.
  tabbableChips?: boolean;
  /**
   * Single-select only. Arrow keys move the selection with the focus; set `false`
   * where landing on an option is expensive, to move focus alone and select on
   * Space or Enter. ARIA allows both.
   */
  selectsOnArrowKeys?: boolean;
  size?: ChipSize;
  selectedIcon?: SvgIcon | null; // Optional icon to display on selected chip(s)
  selectedValues?: Array<string | number>;
  separatorIndices?: number[];
  shouldWrap?: boolean;
  onChange: (newValue: Array<string | number> | (string | number) | null) => void;
  nativePlatform?: 'android' | 'ios';
  chipGap?: SpacingSystemType;
  chipPaddingX?: SpacingSystemType | [SpacingSystemType, SpacingSystemType];
  chipPaddingY?: SpacingSystemType | [SpacingSystemType, SpacingSystemType];
  chipWidth?: SizeSystemType;
  /** Stretches the chips to fill the row between them. */
  fullWidthChips?: boolean;
  fontWeight?: 'regular' | 'semibold';
  fieldError?: FeedbackMessageProps['fieldError'];
};

const WebSeparatorHeightMap: Record<ChipSize, string> = {
  small: '30px',
  default: '44px',
};
const calculateSeparatorHeight = (s: ChipSize): string =>
  isReactNative() ? '100%' : WebSeparatorHeightMap[s];

// Compares contents: callers rebuild `selectedValues` every render, in no fixed order.
const selectionKey = (values: Array<string | number>): string =>
  values
    .map((value) => JSON.stringify(value))
    .sort()
    .join();

const FilterChipGroup: React.FC<FilterChipGroupProps> = ({
  groupLabel,
  groupSecondaryLabel,
  describedById,
  showLabel = true,
  options,
  canSelectMultiple,
  isDeselectable = canSelectMultiple,
  tabbableChips = false,
  selectsOnArrowKeys = true,
  size = 'default',
  selectedIcon = Check,
  selectedValues = [],
  separatorIndices = [],
  shouldWrap = true,
  onChange,
  nativePlatform,
  chipGap = '2x',
  chipPaddingX,
  chipPaddingY,
  chipWidth,
  fullWidthChips = false,
  fontWeight,
  fieldError,
}) => {
  const { t } = useLingui();
  // Two groups on the same screen would otherwise share one legend id, and the
  // second would take its accessible name from the first group's question.
  const labelId = useId();
  const [selected, setSelected] = React.useState<Array<string | number>>(selectedValues);
  // The chips follow each change to what `selectedValues` holds, and keep a click
  // in between, so a caller need not pass every change back.
  const incomingKey = selectionKey(selectedValues);
  const [syncedKey, setSyncedKey] = React.useState(incomingKey);
  if (incomingKey !== syncedKey) {
    setSyncedKey(incomingKey);
    setSelected(selectedValues);
  }
  const [chipRefs, setChipRefs] = React.useState<Array<React.RefObject<ChipType>>>([]);
  const [tabIndex, setTabIndex] = React.useState<number>(0);
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);

  if (canSelectMultiple && !isDeselectable) {
    throw new Error(
      'FilterChipGroup with "canSelectMultiple" true must also have "isDeselectable" true.'
    );
  }

  useEffect(() => {
    // Initialize refs for each chip
    setChipRefs((prevRefs) =>
      options.map((_, idx) => prevRefs[idx] || React.createRef<ChipType>())
    );
  }, [options]);

  // A group that can be emptied is checkboxes, not radios: radios cannot be empty.
  const isCheckbox = canSelectMultiple || isDeselectable;

  const isSelected = (value: string | number): boolean => selected.includes(value);

  // Reports every activation, including of the chip already chosen, so a caller
  // can retry what it did with it.
  const handleClick = (optionIndex: number): void => {
    const { value } = options[optionIndex];
    if (canSelectMultiple) {
      const next = options
        .filter((option) =>
          option.value === value ? !isSelected(value) : isSelected(option.value)
        )
        .map((option) => option.value);
      setSelected(next);
      onChange(next);
    } else {
      const next = isDeselectable && isSelected(value) ? null : value;
      setSelected(next === null ? [] : [next]);
      onChange(next);
    }
  };

  // Skips disabled options: Chip ignores their clicks, so the selection would not follow.
  const nextRadioIndex = (from: number, step: number): number | null => {
    for (let offset = 1; offset < options.length; offset += 1) {
      const candidate =
        (((from + step * offset) % options.length) + options.length) % options.length;
      if (!options[candidate]?.isDisabled) return candidate;
    }

    return null;
  };

  // Tab has to leave from wherever the focus is, and under manual activation that is
  // not always the checked chip, so the group's one tab stop follows the focus.
  const handleFocus = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (isCheckbox) return;
    const chips = Array.from(
      event.currentTarget.querySelectorAll('[data-testid="kibble-patterns-chip"]')
    );
    const index = chips.indexOf(event.target as Element);
    setFocusedIndex(index === -1 ? null : index);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (isCheckbox) return;
    // Leaving the group hands the tab stop back to the checked chip, so returning
    // by Tab lands on the choice in force.
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusedIndex(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    // Find all chip buttons within this fieldset
    const fieldset = event.currentTarget;
    const chipButtons = Array.from(
      fieldset.querySelectorAll('[data-testid="kibble-patterns-chip"]')
    ) as HTMLButtonElement[];
    const focusedChipIndex = chipButtons.findIndex((button) => button === document.activeElement);

    if (focusedChipIndex === -1) return;

    switch (event.key) {
      case KEYS.DOWN:
      case KEYS.RIGHT:
      case KEYS.UP:
      case KEYS.LEFT: {
        const step = event.key === KEYS.DOWN || event.key === KEYS.RIGHT ? 1 : -1;

        // Checkbox arrows move focus only, and stop at each end instead of wrapping.
        if (isCheckbox) {
          const next = focusedChipIndex + step;
          if (next >= 0 && next < chipButtons.length) {
            event.preventDefault();
            chipButtons[next]?.focus();
          }
          break;
        }

        event.preventDefault();
        const next = nextRadioIndex(focusedChipIndex, step);
        if (next === null) break;

        chipButtons[next]?.focus();
        if (selectsOnArrowKeys) handleClick(next);
        break;
      }

      case KEYS.SPACE:
      case KEYS.ENTER:
        // Don't prevent the default behavior
        break;

      case KEYS.TAB:
        // With `tabbableChips`, leave every chip at tabIndex 0 so Tab moves between
        // chips one at a time (each is its own Tab stop). Otherwise drop all chips
        // out of the tab order so Tab exits the group as a single composite widget,
        // then restore tabIndex once focus has left so it can return later.
        // A radio group needs none of this: its roving tabIndex keeps one chip tabbable.
        if (!tabbableChips && isCheckbox) {
          setTabIndex(-1);
          setTimeout(() => setTabIndex(0), 100);
        }
        break;

      default:
        event.preventDefault();
        break;
    }
  };

  const chipRole = isCheckbox ? 'checkbox' : 'radio';

  // One tab stop: the focused chip, else the checked one, else the first so an
  // unanswered group is reachable.
  const checkedIndex = options.findIndex((option) => isSelected(option.value));
  const tabStopIndex = focusedIndex ?? (checkedIndex === -1 ? 0 : checkedIndex);
  const rovingTabIndex = (index: number): number => (index === tabStopIndex ? 0 : -1);
  const webA11yProps = {
    role: isCheckbox ? 'group' : 'radiogroup',
    'aria-describedby': describedById,
    'aria-label': showLabel ? undefined : groupLabel,
    'aria-labelledby': showLabel ? labelId : undefined,
  };
  const rxnA11yProps = {
    // there's no 'checkboxgroup' AccessibiltyRole, nor even a 'group' role, so we use 'checkbox' :wtf:
    accessibilityRole: (isCheckbox ? 'checkbox' : 'radiogroup') as AccessibilityRole,
    // but Role has a 'group'. What's the difference? ¯\_ (ツ)_/¯
    role: (isCheckbox ? 'group' : 'radiogroup') as Role,
  };

  // iOS RxN doesn't announce these roles via accessibility APIs, so we do it manually via the hint
  const roleA11yHint = isCheckbox ? t`checkbox` : t`radio button`;
  // and then add the group label if it's hidden, since iOS doesn't focus the group either
  const iOSA11yHint = showLabel ? roleA11yHint : `${groupLabel}, ${roleA11yHint}`;

  return (
    <Flex
      webAs="fieldset"
      rxnProps={rxnA11yProps}
      webProps={webA11yProps}
      flexDirection="column"
      margin="0x"
      mb="4x"
      padding="0x"
      border="none"
      data-testid="filter-chip-group-fieldset"
      webAsProps={{
        onKeyDown: handleKeyDown,
        onFocus: handleFocus,
        onBlur: handleBlur,
      }}
    >
      {showLabel && (
        <PrimaryLabel as="legend" id={labelId} primaryLabel={groupLabel} required={false} />
      )}
      {groupSecondaryLabel && <SecondaryLabel secondaryLabel={groupSecondaryLabel} />}
      {fieldError && (
        <Flex mb="3x">
          <FeedbackMessage fieldError={fieldError} />
        </Flex>
      )}
      <Flex
        alignItems="center"
        sx={{ flexFlow: `row ${shouldWrap ? 'wrap' : ''}`, gap: chipGap }}
        data-testid="filter-chip-group"
      >
        {options.map((option, idx) => (
          <React.Fragment key={option.id}>
            <FilterChip
              key={option.id}
              chipRef={chipRefs[idx]}
              {...option}
              icon={option.icon}
              isSelected={isSelected(option.value)}
              selectedIcon={selectedIcon ?? undefined}
              role={chipRole}
              size={size}
              tabIndex={isCheckbox ? tabIndex : rovingTabIndex(idx)}
              onClick={() => handleClick(idx)}
              accessibilityHint={nativePlatform === 'ios' ? iOSA11yHint : undefined}
              paddingX={chipPaddingX}
              paddingY={chipPaddingY}
              width={chipWidth}
              fullWidth={fullWidthChips}
              fontWeight={fontWeight}
            />

            {separatorIndices.includes(idx) && (
              <Flex
                width="1px"
                height={calculateSeparatorHeight(size)}
                borderColor="separator.primary"
                borderRight="1px solid"
                borderRightWidth="1px" // RxN needs this defined independently of `borderRight`
                mx="2x"
                data-testid="kibble-patterns-chip-separator"
              />
            )}
          </React.Fragment>
        ))}
      </Flex>
    </Flex>
  );
};

export default FilterChipGroup;
